import React, { useState, useRef } from "react";
import html2canvas from "html2canvas";
import { motion } from "framer-motion";

// Currency conversion
const currencyData = {
  GBP: { symbol: "£", factor: 1 },
  USD: { symbol: "$", factor: 1.25 },
  EUR: { symbol: "€", factor: 1.15 },
  AUD: { symbol: "A$", factor: 1.9 },
  CAD: { symbol: "C$", factor: 1.7 }
};

const ShareableInfographics = () => {
  const [currency, setCurrency] = useState("GBP");
  const [exportScale, setExportScale] = useState(2);
  const [generatingId, setGeneratingId] = useState(null);
  const refs = useRef({});

  // Dynamic infographic text
  const getImpulseSpendText = () => {
    const { symbol, factor } = currencyData[currency];
    const monthly = Math.round(260 * factor);
    const yearly = Math.round(monthly * 12);
    return `The average person spends ${symbol}${monthly}/month on impulse buys. That’s over ${symbol}${yearly}/year.`;
  };

  // Infographics
  const infographicData = [
    {
      id: 1,
      emojiMain: "🧠",
      emojiAccent: "❌💸",
      emojiCorner: "🫣",
      title: "Why We Regret Buying",
      text: "Cognitive dissonance triggers guilt. Your brain hates when wants & values conflict.",
      footer: "Reduce mental strain. Shop intentionally — GuiltGauge.com"
    },
    {
      id: 2,
      emojiMain: "💳",
      emojiAccent: "🛍️📦",
      emojiCorner: "😅",
      title: "Impulse Spending Adds Up",
      text: getImpulseSpendText(),
      footer: "Track your patterns. GuiltGauge.com"
    },
    {
      id: 3,
      emojiMain: "😰",
      emojiAccent: "🫥🧾",
      emojiCorner: "🧃",
      title: "Emotional Spending is Real",
      text: "67% of shoppers buy when stressed or sad. The dopamine hit? Lasts ~30 minutes.",
      footer: "Heal the cause, not the cart. GuiltGauge.com"
    },
    {
      id: 4,
      emojiMain: "📉",
      emojiAccent: "📈📉",
      emojiCorner: "⏰",
      title: "The Decline Curve",
      text: "Excitement peaks before purchase. Regret peaks after. Guilt? Usually on delivery day.",
      footer: "Delay the buy. Break the curve. GuiltGauge.com"
    },
    {
      id: 5,
      emojiMain: "📦",
      emojiAccent: "🚚🔄",
      emojiCorner: "🙈",
      title: "Return Regret",
      text: "48% regret online buys, but only 30% return them. Why? Shame. Hassle. Delay.",
      footer: "Review before checkout. GuiltGauge.com"
    },
    {
      id: 6,
      emojiMain: "👛",
      emojiAccent: "📉💬",
      emojiCorner: "💭",
      title: "Guilt Is Income-Based",
      text: "Low-income shoppers report 3x more guilt — even for essentials.",
      footer: "Guilt is a social issue too. GuiltGauge.com"
    },
    {
      id: 7,
      emojiMain: "🛍️",
      emojiAccent: "😃➡️😬",
      emojiCorner: "🧾",
      title: "Retail Therapy Fades Fast",
      text: "Mood boost fades in 30 mins. Financial stress can last months.",
      footer: "Better therapy: awareness. GuiltGauge.com"
    },
    {
      id: 8,
      emojiMain: "🔁",
      emojiAccent: "😢➡️🛒➡️😬",
      emojiCorner: "🧠",
      title: "The Guilt Loop",
      text: "Feel bad → shop → regret → repeat. It's emotional, not logical.",
      footer: "Break the cycle. GuiltGauge.com"
    },
    {
      id: 9,
      emojiMain: "💡",
      emojiAccent: "📊📱",
      emojiCorner: "🧘‍♀️",
      title: "Tracking Reduces Spend",
      text: "People who track purchases spend up to 23% less. Awareness creates friction.",
      footer: "Track without shame. GuiltGauge.com"
    },
    {
      id: 10,
      emojiMain: "🕵️",
      emojiAccent: "🤔🛒",
      emojiCorner: "⏳",
      title: "Mindful vs. Mindless",
      text: "Mindless = fast, automatic. Mindful = slow, intentional. Guilt hates reflection.",
      footer: "Pause before purchase. GuiltGauge.com"
    }
  ];

  const generateImage = async (id) => {
    const ref = refs.current[id];
    if (!ref) return;

    setGeneratingId(id);

    try {
      // Temporarily make the export version visible for rendering
      ref.style.visibility = "visible";
      ref.style.position = "fixed";
      ref.style.top = "0";
      ref.style.left = "0";
      ref.style.zIndex = "-1";

      // Add a small delay to ensure DOM is rendered
      await new Promise((resolve) => setTimeout(resolve, 100));

      const canvas = await html2canvas(ref, {
        backgroundColor: "#ffffff",
        width: 800,
        height: 800,
        scale: exportScale,
        logging: false,
        useCORS: true,
        allowTaint: true
      });

      // Restore hidden state
      ref.style.visibility = "hidden";
      ref.style.position = "absolute";
      ref.style.top = "-9999px";
      ref.style.left = "-9999px";
      ref.style.zIndex = "-1";

      const imgUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `guilt-gauge-infographic-${id}@${exportScale}x.png`;
      link.href = imgUrl;
      link.click();
    } catch (error) {
      console.error("Error generating image:", error);
    } finally {
      setGeneratingId(null);
    }
  };

  const shareImage = async (id) => {
    const ref = refs.current[id];
    if (!ref) return;

    try {
      // Temporarily make the export version visible for rendering
      ref.style.visibility = "visible";
      ref.style.position = "fixed";
      ref.style.top = "0";
      ref.style.left = "0";
      ref.style.zIndex = "-1";

      // Add a small delay to ensure DOM is rendered
      await new Promise((resolve) => setTimeout(resolve, 100));

      const canvas = await html2canvas(ref, {
        backgroundColor: "#ffffff",
        width: 800,
        height: 800,
        scale: exportScale,
        logging: false,
        useCORS: true,
        allowTaint: true
      });

      // Restore hidden state
      ref.style.visibility = "hidden";
      ref.style.position = "absolute";
      ref.style.top = "-9999px";
      ref.style.left = "-9999px";
      ref.style.zIndex = "-1";

      const blob = await (await fetch(canvas.toDataURL("image/png"))).blob();
      const file = new File([blob], `guilt-gauge-infographic-${id}.png`, {
        type: "image/png"
      });

      const shareData = {
        title: "Shopping Guilt Awareness",
        text: "Check out this stat from GuiltGauge.com",
        url: "https://guiltgauge.com",
        files: [file]
      };

      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        alert("Your browser doesn’t support file sharing.");
      }
    } catch (error) {
      console.error("Error sharing image:", error);
    }
  };

  return (
    <div className="mt-6">
      <h2 className="text-2xl font-bold text-center mb-4 dark:text-white">
        Shareable Infographics
      </h2>

      <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-6">
        Tap below to download and share square-format statistics on shopping guilt.
      </p>

      {/* Options */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <label className="mr-2">Currency:</label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm"
          >
            {Object.keys(currencyData).map((cur) => (
              <option key={cur} value={cur}>
                {currencyData[cur].symbol} {cur}
              </option>
            ))}
          </select>
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400">
          <label className="mr-2">Image Quality:</label>
          <select
            value={exportScale}
            onChange={(e) => setExportScale(parseInt(e.target.value))}
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm"
          >
            <option value={2}>Standard (2×)</option>
            <option value={3}>Retina (3×)</option>
            <option value={4}>Ultra (4×)</option>
          </select>
        </div>
      </div>

      {/* Infographic cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {infographicData.map((info) => (
          <div key={info.id} className="w-full">
            {/* Visible card with fixed ratio */}
            <div className="aspect-square relative w-full bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md text-center overflow-hidden">
              {/* Top-left emoji */}
              <div className="absolute top-4 left-4 text-3xl z-10">{info.emojiMain}</div>
      
              {/* Bottom-right icon */}
              <div className="absolute bottom-4 right-4 text-2xl opacity-70 z-10">{info.emojiCorner}</div>
      
              {/* Large faded background */}
              <div className="absolute inset-0 text-5xl opacity-10 flex items-center justify-center pointer-events-none select-none">
                {info.emojiAccent}
              </div>
      
              <div className="z-10 mt-auto mb-auto px-2 pt-10">
                <h3 className="text-lg font-bold mb-1">{info.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{info.text}</p>
                <p className="text-xs text-gray-400 mt-3 italic">{info.footer}</p>
              </div>
            </div>
      
            {/* Buttons */}
            <div className="flex justify-center mt-2 gap-3">
              <motion.button
                onClick={() => generateImage(info.id)}
                className="py-2 px-3 bg-emerald-500 hover:bg-emerald-600 text-white text-xs rounded-lg font-semibold"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={generatingId === info.id}
              >
                {generatingId === info.id ? "Generating..." : "📷 Download"}
              </motion.button>
      
              {navigator.share && (
                <motion.button
                  onClick={() => shareImage(info.id)}
                  className="py-2 px-3 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-lg font-semibold"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  📱 Share
                </motion.button>
              )}
            </div>
      
            {/* Hidden export version (800x800 px) */}
            <div
              ref={(el) => (refs.current[info.id] = el)}
              style={{
                position: "absolute",
                width: "800px",
                height: "800px",
                visibility: "hidden",
                top: "-9999px",
                left: "-9999px",
                zIndex: -1,
                backgroundColor: "#ffffff"
              }}
            >
              <div className="relative w-[800px] h-[800px] bg-white rounded-xl p-12 shadow-md text-center overflow-hidden">
                <div className="absolute top-8 left-8 text-6xl z-10">{info.emojiMain}</div>
                <div className="absolute bottom-8 right-8 text-4xl opacity-70 z-10">{info.emojiCorner}</div>
                <div className="absolute inset-0 text-9xl opacity-10 flex items-center justify-center pointer-events-none select-none">
                  {info.emojiAccent}
                </div>
                <div className="z-10 mt-auto mb-auto px-4 pt-20">
                  <h3 className="text-3xl font-bold mb-4">{info.title}</h3>
                  <p className="text-xl text-gray-700 leading-relaxed">{info.text}</p>
                  <p className="text-lg text-gray-400 mt-6 italic">{info.footer}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center text-xs text-gray-500 dark:text-gray-400">
        All infographics © GuiltGauge.com | Free to share with attribution.
      </div>
    </div>
  );
};

export default ShareableInfographics;
